<script>
import { useElixioConnectStore } from '../../stores/elixioConnect'
import { extractDeepErrorMessage } from '../../composables/useApiErrors'
import EcButton from '../ui/EcButton.vue'
import EcErrorBanner from '../ui/EcErrorBanner.vue'
import { PICKUP_LOCATIONS } from '../../types/shipment'

export default {
  name: 'PickupForm',
  components: { EcButton, EcErrorBanner },
  props: {
    shipment: { type: Object, required: true },
    initialDetails: { type: Object, default: null },
  },
  data() {
    return {
      store: useElixioConnectStore(),
      submitting: false,
      errors: [],
      PICKUP_LOCATIONS,
      form: this.initialDetails
        ? JSON.parse(JSON.stringify(this.initialDetails))
        : this.buildDefaults(),
    }
  },
  computed: {
    t() {
      return this.$ecTranslate || ((k) => k)
    },
    notify() {
      return this.$ecNotify || (() => {})
    },
    pickupDate: {
      get() {
        const d = this.form.pickup_details?.date
        if (!d) return ''
        return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`
      },
      set(val) {
        if (!val) return
        const [year, month, day] = val.split('-').map(Number)
        this.form.pickup_details.date = { year, month, day }
      },
    },
    readyAt: {
      get() {
        const t = this.form.pickup_details?.ready_at
        if (!t) return ''
        return `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`
      },
      set(val) {
        const [hour, minute] = (val || '0:0').split(':').map(Number)
        this.form.pickup_details.ready_at = { hour, minute }
      },
    },
    readyUntil: {
      get() {
        const t = this.form.pickup_details?.ready_until
        if (!t) return ''
        return `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`
      },
      set(val) {
        const [hour, minute] = (val || '0:0').split(':').map(Number)
        this.form.pickup_details.ready_until = { hour, minute }
      },
    },
  },
  methods: {
    pickupLocationLabel(loc) {
      const snake = loc.replace(/([A-Z])/g, '_$1').toUpperCase().replace(/^_/, '')
      return this.t('ELIXIO_CONNECT.PICKUP_' + snake)
    },
    buildDefaults() {
      const packages = this.shipment?.packaging_properties?.packages ?? []
      const totalWeight = packages.reduce(
        (sum, p) => sum + Number(p.weight || 0) * Number(p.quantity || 1),
        0,
      )
      const totalQuantity = packages.reduce(
        (sum, p) => sum + Number(p.quantity || 1),
        0,
      )
      const now = new Date()

      return {
        pickup_details: {
          date: { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() },
          ready_at: { hour: 9, minute: 0 },
          ready_until: { hour: 17, minute: 0 },
          package_details: {
            weight: {
              unit: this.shipment?.packaging_properties?.weight?.unit ?? 'lb',
              value: String(totalWeight),
            },
            quantity: String(totalQuantity),
          },
          pickup_location: 'FrontDoor',
          pickup_instructions: '',
        },
      }
    },
    async submit() {
      this.errors = []
      this.submitting = true
      try {
        await this.store.createPickup(this.shipment.id, this.form)
        this.notify(this.t('ELIXIO_CONNECT.PICKUP_CREATED'), 'success')
        this.$emit('submitted')
      } catch (e) {
        this.errors = [extractDeepErrorMessage(e, this.t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG'))]
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<template>
  <form class="ec-pickup-form" @submit.prevent="submit">
    <EcErrorBanner v-if="errors.length" :errors="errors" class="ec-mb-3" />

    <div class="ec-form-group">
      <label class="ec-form-label">{{ t('ELIXIO_CONNECT.PICKUP_DATE') }}</label>
      <input v-model="pickupDate" type="date" class="ec-input__field" />
    </div>

    <div class="ec-form-row">
      <div class="ec-form-group">
        <label class="ec-form-label">{{ t('ELIXIO_CONNECT.READY_AT') }}</label>
        <input v-model="readyAt" type="time" class="ec-input__field" />
      </div>
      <div class="ec-form-group">
        <label class="ec-form-label">{{ t('ELIXIO_CONNECT.READY_UNTIL') }}</label>
        <input v-model="readyUntil" type="time" class="ec-input__field" />
      </div>
    </div>

    <div class="ec-form-row">
      <div class="ec-form-group">
        <label class="ec-form-label">
          {{ t('ELIXIO_CONNECT.TOTAL_WEIGHT') }} ({{ shipment.packaging_properties?.weight?.unit ?? 'lb' }})
        </label>
        <input v-model="form.pickup_details.package_details.weight.value" type="text" class="ec-input__field" />
      </div>
      <div class="ec-form-group">
        <label class="ec-form-label">{{ t('ELIXIO_CONNECT.PACKAGE_COUNT') }}</label>
        <input v-model="form.pickup_details.package_details.quantity" type="text" class="ec-input__field" />
      </div>
    </div>

    <div class="ec-form-group">
      <label class="ec-form-label">{{ t('ELIXIO_CONNECT.PICKUP_LOCATION') }}</label>
      <select v-model="form.pickup_details.pickup_location" class="ec-select__field">
        <option v-for="loc in PICKUP_LOCATIONS" :key="loc" :value="loc">
          {{ pickupLocationLabel(loc) }}
        </option>
      </select>
    </div>

    <div class="ec-form-group">
      <label class="ec-form-label">{{ t('ELIXIO_CONNECT.INSTRUCTIONS') }}</label>
      <textarea v-model="form.pickup_details.pickup_instructions" class="ec-textarea" rows="3" />
    </div>

    <div class="ec-flex ec-justify-end ec-gap-2 ec-mt-3">
      <EcButton variant="outline-secondary" @click="$emit('cancel')">
        {{ t('ELIXIO_CONNECT.CANCEL') }}
      </EcButton>
      <EcButton variant="primary" :loading="submitting" @click="submit">
        {{ t('ELIXIO_CONNECT.CREATE_PICKUP') }}
      </EcButton>
    </div>
  </form>
</template>
